// / <reference types="google.maps" />

import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import styled from '@emotion/styled';
import { DEFAULT_LATLNG } from 'constants/default-config';
import { useTranslation } from 'react-i18next';

type MapsProps = {
    defaultCenter?: { lat: number; lng: number };
    defaultZoom?: number;
    center?: { lat: number; lng: number };
    zoom?: number;
    options?: google.maps.MapOptions;
    onGoogleApiLoaded?: (map: google.maps.Map) => void;
    onZoomChanged?: (zoom: number, map: google.maps.Map) => void;
    onDragEnd?: (latlng: { lat: () => number; lng: () => number }, map: google.maps.Map) => void;
} & Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onDragEnd'>;
//  @see options see https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions

function Maps({
    defaultCenter = { lat: DEFAULT_LATLNG.latitude, lng: DEFAULT_LATLNG.longitude },
    defaultZoom = 0,
    center,
    zoom = 0,
    options,
    onGoogleApiLoaded,
    onDragEnd,
    onZoomChanged,
    children,
    ...props
}: MapsProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [maps, setMaps] = useState<google.maps.Map>();
    const [drag, setDrag] = useState<google.maps.LatLng | undefined>();
    const [zooms, setZooms] = useState<number | undefined>(0);

    useEffect(() => {
        const initialMap = () => {
            if (typeof window !== 'undefined' && window?.google && ref.current && !maps) {
                const _map: google.maps.Map = new window.google.maps.Map(ref.current, {
                    center: defaultCenter,
                    zoom: defaultZoom,
                    gestureHandling: 'cooperative',
                    zoomControl: false,
                    fullscreenControl: false,
                    mapTypeControl: false,
                    //overviewMapControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    ...options,
                });

                _map.addListener('dragend', () => {
                    setDrag(_map.getCenter());
                });
                _map.addListener('zoom_changed', () => {
                    setZooms(_map.getZoom());
                });
                if (center) _map.panTo(center);
                if (zoom) _map.setZoom(zoom);

                if (onGoogleApiLoaded) {
                    onGoogleApiLoaded(_map);
                }

                setMaps(_map);
            }
        };
        initialMap();
    }, [ref.current]);

    useEffect(() => {
        const onSetCenter = () => {
            if (maps && center && maps.panTo) {
                maps.panTo(center);
            }
        };

        onSetCenter();
    }, [maps, JSON.stringify(center)]);

    useEffect(() => {
        if (maps && drag && onDragEnd) onDragEnd(drag, maps);
    }, [drag]);

    useEffect(() => {
        if (maps && zooms && onZoomChanged) onZoomChanged(zooms, maps);
    }, [zooms]);

    return (
        <>
            <Style ref={ref} {...props} />
            {children}
        </>
    );
}

function GoogleMaps(props: MapsProps) {
    const { i18n } = useTranslation();
    const render = (status: Status) => {
        console.log('status:', status);
        if (status === Status.LOADING) {
            return <div>maps loading...</div>;
        }
        if (status === Status.FAILURE) {
            return <div>maps error</div>;
        }
        if (status === Status.SUCCESS) {
            return <div>maps loaded</div>;
        }

        return <></>;
    };
    return (
        <Wrapper
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY ?? ''}
            language={i18n.language?.toLowerCase()}
            libraries={['places', 'geometry']}
            render={render}
        >
            <Maps {...props} />
        </Wrapper>
    );
}

export default GoogleMaps;

const Style = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;
